import React, {useEffect, useState} from "react";
import Dropdown from 'react-bootstrap/Dropdown';
import {Checkbox, ConfigProvider, DatePicker} from 'antd';
import {FilterDropdownIcon} from "../../../../assets/icons/defaultIcons";
import "./filter-dropdown.scss";
import "./vault-filter-dropdown.scss";

const { RangePicker } = DatePicker;

// Custom theme for the date picker and checkbox to match the design system
const customTheme = {
    components: {
        DatePicker: {
            // Using the app's color variables through the theme
            colorPrimary: "#D2A84E",
            colorBgContainer: "#353535",
            colorBgElevated: "#272C2C",
            colorTextDisabled: "#525050",
            colorIcon: "#929399",
            colorIconHover: "#D2A84E",
            boxShadowSecondary: "none",
            cellActiveWithRangeBg: "rgba(210, 168, 78, 0.15)",
            cellHoverWithRangeBg: "rgba(210, 168, 78, 0.2)",
            cellRangeBorderColor: "#D2A84E",
            cellBgSelected: "#D2A84E",
            cellTextSelected: "#000",
            colorBorder: "#3d3d3d",
            colorPrimaryBorder: "#D2A84E",
            colorPrimaryHover: "#D2A84E",
            colorPrimaryText: "#D2A84E",
            colorLink: "#D2A84E",
            colorLinkHover: "#FDD017",
            colorLinkActive: "#D2A84E",
            colorSplit: "#3d3d3d"
        },
        Checkbox: {
            colorPrimary: "#D2A84E",
            borderRadiusSM: 4, // Rounded checkbox with 8px radius
            colorBgContainer: "transparent",
            colorBorder: "#3d3d3d",
            controlInteractiveSize: 20, // 16px size
            controlHeight: 20, // 16px height
            fontSize: 14 // Text size
        }
    }
};

export default function VaultFilterDropdown({ onFilterChange }) {
    const [showDropdown, setShowDropdown] = useState(false);
    const [dateRange, setDateRange] = useState(null);
    const [partiallyWatched, setPartiallyWatched] = useState(false);
    const [fullyWatched, setFullyWatched] = useState(false);
    const [filtersApplied, setFiltersApplied] = useState(false);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

    // Format date for API
    const formatDate = (date) => {
        if (!date) return null;
        return date.format('YYYY-MM-DD');
    };

    const toggleDropdown = () => {
        // If closing dropdown, also close date picker
        if (showDropdown && isDatePickerOpen) {
            setIsDatePickerOpen(false);
        }
        setShowDropdown(!showDropdown);
    };

    const handleRangeChange = (dates, dateStrings) => {
        // Prevent event propagation to stop dropdown from closing
        if (window.event) {
            window.event.stopPropagation();
            window.event.preventDefault();
        }
        
        setDateRange(dates);
        
        // If dates are selected and date picker would close automatically, keep dropdown open
        if (dates && dates.length === 2) {
            // We'll handle this in onOpenChange of the RangePicker
        }
    };

    const handlePartiallyWatchedChange = (e) => {
        e.stopPropagation();
        if (e.target.checked) {
            setPartiallyWatched(true);
            setFullyWatched(false); // Uncheck the other option
        } else {
            setPartiallyWatched(false);
        }
    };

    const handleFullyWatchedChange = (e) => {
        e.stopPropagation();
        if (e.target.checked) {
            setFullyWatched(true);
            setPartiallyWatched(false); // Uncheck the other option
        } else {
            setFullyWatched(false);
        }
    };

    const applyFilters = () => {
        // Create filter object with only defined values
        const filters = {};
        
        if (dateRange && dateRange[0]) {
            filters.start_date = formatDate(dateRange[0]);
        }
        
        if (dateRange && dateRange[1]) {
            filters.end_date = formatDate(dateRange[1]);
        }
        
        if (partiallyWatched) {
            filters.partially_watched = partiallyWatched;
        }
        
        if (fullyWatched) {
            filters.fully_watched = fullyWatched;
        }

        // Notify parent component about filter changes
        if (onFilterChange) {
            onFilterChange(filters);
        }

        // Track if any filters are applied for UI indication
        setFiltersApplied(
            !!dateRange || partiallyWatched || fullyWatched
        );

        // Close date picker if it's open
        if (isDatePickerOpen) {
            setIsDatePickerOpen(false);
        }
        
        toggleDropdown();
    };

    const resetFilters = () => {
        setDateRange(null);
        setPartiallyWatched(false);
        setFullyWatched(false);
        setFiltersApplied(false);
        
        // Close date picker if it's open
        if (isDatePickerOpen) {
            setIsDatePickerOpen(false);
        }
        
        // Notify parent component that filters were reset
        if (onFilterChange) {
            onFilterChange({});
        }
    };

    // Add event listener to handle clicks outside when date picker is open
    useEffect(() => {
        const handleGlobalClick = (e) => {
            // If date picker is open, prevent closing the dropdown
            // unless the click is on the Apply or Reset button
            if (isDatePickerOpen && 
                !e.target.closest('.ant-picker-dropdown') && 
                !e.target.closest('.date-range-picker') &&
                !e.target.closest('.btn-outline')) {
                
                // Close the date picker
                setIsDatePickerOpen(false);
            }
        };

        if (isDatePickerOpen) {
            document.addEventListener('click', handleGlobalClick);
        }

        return () => {
            document.removeEventListener('click', handleGlobalClick);
        };
    }, [isDatePickerOpen]);

    // Update UI for selected dates
    useEffect(() => {
        // Add a small delay to ensure styles are applied after render
        if (dateRange && dateRange.length) {
            setTimeout(() => {
                // Find input elements and update their styling if needed
                const inputElements = document.querySelectorAll('.vault-date-range-picker .ant-picker-input input');
                inputElements.forEach(input => {
                    if (input.value) {
                        input.style.color = 'var(--font-color-dark-1, #fff)';
                    }
                });
            }, 50);
        }
    }, [dateRange]);

    return (
        <div className={`vault-filter-dropdown ${isDatePickerOpen ? 'date-picker-open' : ''}`}>
            <Dropdown 
                show={showDropdown}
                onToggle={(isOpen, event, metadata) => {
                    // Prevent toggling when clicking within the dropdown menu
                    if (event && event.target && event.target.closest('.vault-filters')) {
                        return;
                    }
                    
                    // Prevent closing the dropdown when date picker is open
                    if (!isOpen && isDatePickerOpen) {
                        return; // Don't close dropdown if date picker is open
                    }
                    
                    setShowDropdown(isOpen);
                }}
            >
                <Dropdown.Toggle variant="filter" id="dropdown-basic" className={filtersApplied ? 'has-filters' : ''}>
                    <FilterDropdownIcon />
                    {/*<span className="filter-text">Filter </span> */}
                    {filtersApplied && <span className="filter-badge"></span>}
                    {/*<span className="filter-down-icon"><FilterDropdownDownIcon /></span>*/}
                </Dropdown.Toggle>

                <Dropdown.Menu className={showDropdown ? 'show-animation' : 'hide-animation'}>
                    <div className="filter-dropdown-header">
                        <div>
                            <h4>Filter by</h4>
                        </div>
                        <div className="drop-actions">
                            <button className="btn btn-text" onClick={resetFilters}>Reset</button>
                            <button className="btn btn-light btn-small" onClick={applyFilters}>Apply</button>
                        </div>
                    </div>
                    
                    <div className="filter-dropdown-body">
                        <div className="vault-filters">
                            <div className="filter-section">
                                <h5>Date Range</h5>
                                <div className="date-range-picker">
                                    <ConfigProvider theme={customTheme}>
                                        <RangePicker 
                                            value={dateRange}
                                            onChange={handleRangeChange}
                                            format="YYYY-MM-DD"
                                            onClick={(e) => e.stopPropagation()}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onOpenChange={(open) => setIsDatePickerOpen(open)}
                                            open={isDatePickerOpen}
                                            getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                            placeholder={['Start Date', 'End Date']}
                                            style={{ 
                                                backgroundColor: '#353535',
                                                borderColor: '#3d3d3d'
                                            }}
                                            popupStyle={{ 
                                                backgroundColor: '#272C2C',
                                                boxShadow: 'none'
                                            }}
                                            className="vault-date-range-picker"
                                        />
                                    </ConfigProvider>
                                </div>
                            </div>
                            
                            <div className="filter-section">
                                <h5>Watch Status</h5>
                                <div className="watch-status-filters">
                                    <ConfigProvider theme={customTheme}>
                                        <Checkbox 
                                            checked={partiallyWatched}
                                            onChange={handlePartiallyWatchedChange}
                                            onClick={(e) => e.stopPropagation()}
                                            className="vault-checkbox"
                                        >
                                            Partially Watched
                                        </Checkbox>
                                        <Checkbox 
                                            checked={fullyWatched}
                                            onChange={handleFullyWatchedChange}
                                            onClick={(e) => e.stopPropagation()}
                                            className="vault-checkbox"
                                        >
                                            Fully Watched
                                        </Checkbox>
                                    </ConfigProvider>
                                </div>
                            </div>
                        </div>
                    </div>
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );
}
